import { ParentState } from "@capone/checkout";
import { BookingQuestionId, Person, PersonId } from "redmond";

import { ActivityContext } from "../../../modules/book/state/types";

/**
 * Booking question IDs that are handled in the traveler information step
 */
export const HANDLED_PER_TRAVELER_BOOKING_QUESTIONS: BookingQuestionId[] = [
    BookingQuestionId.Height,
    BookingQuestionId.Weight,
    BookingQuestionId.PassportPassportNo,
];

/**
 * Determines which booking questions are required based on the activity's configuration
 */
export const getRequiredBookingQuestions = (ctx: ActivityContext) => {
    const bookingQuestions =
        ctx.activityShop?.bookingQuestions?.perTraveler || [];

    const handledBookingQuestionIds = bookingQuestions
        .filter((question) =>
            HANDLED_PER_TRAVELER_BOOKING_QUESTIONS.includes(question.id)
        )
        .map((question) => question.id);

    return {
        requirePassport: handledBookingQuestionIds.includes(
            BookingQuestionId.PassportPassportNo
        ),
        requireHeight: handledBookingQuestionIds.includes(BookingQuestionId.Height),
        requireWeight: handledBookingQuestionIds.includes(BookingQuestionId.Weight),
    };
};

/**
 * Checks if a passport has all valid fields filled:
 * - Number: not empty, 6-9 characters
 * - Expiration: not empty
 * - Country of issue: not empty
 */
export const hasValidPassport = (passport?: Person["passport"]): boolean => {
    const trimmedNumber = passport?.number?.trim() || "";
    const isValidLength = trimmedNumber.length >= 6 && trimmedNumber.length <= 9;

    return !!(
        trimmedNumber &&
        isValidLength &&
        passport?.expiration?.trim() &&
        passport?.countryOfIssue?.trim()
    );
};

/**
 * Checks if a traveler has all required booking question fields filled with valid values
 */
export const travelerHasAllRequiredFields = (
    traveler: Person,
    ctx: ActivityContext
): boolean => {
    const { requirePassport, requireHeight, requireWeight } =
        getRequiredBookingQuestions(ctx);

    const { bookingQuestionResponse } =
        ctx[ParentState.activityTravelerInformation];
    const { weight, heightFt, heightIn } =
        bookingQuestionResponse?.[traveler.id] || {};

    const passportValid = !requirePassport || hasValidPassport(traveler.passport);
    const hasValidHeight = !requireHeight || (!!heightFt && !!heightIn);
    const hasValidWeight = !requireWeight || !!weight;

    return passportValid && hasValidHeight && hasValidWeight;
};

/**
 * Checks if a traveler is missing any required booking question fields
 * (inverse of travelerHasAllRequiredFields)
 */
export const travelerHasMissingRequiredFields = (
    traveler: Person,
    ctx: ActivityContext
): boolean => !travelerHasAllRequiredFields(traveler, ctx);

/**
 * Checks if a traveler has answered all required booking questions
 * Used by the selector to determine if the form modal should open
 */
export const checkTravelerBookingQuestions = (
    travelerId: PersonId,
    ctx: ActivityContext
): boolean => {
    const { requirePassport, requireHeight, requireWeight } =
        getRequiredBookingQuestions(ctx);

    // If no booking questions are required, return true
    if (!requirePassport && !requireHeight && !requireWeight) {
        return true;
    }

    const { bookingQuestionResponse, userTravelers } =
        ctx[ParentState.activityTravelerInformation];
    const { weight, heightFt, heightIn } =
        bookingQuestionResponse?.[travelerId] || {};

    // Look up the traveler by ID to check their passport
    const traveler = userTravelers.find((t) => t.id === travelerId);

    const passportValid = !requirePassport || hasValidPassport(traveler?.passport);
    const hasValidHeight = !requireHeight || (!!heightFt && !!heightIn);
    const hasValidWeight = !requireWeight || !!weight;

    return passportValid && hasValidHeight && hasValidWeight;
};

/**
 * Validates all selected travelers have all required fields filled
 */
export const allSelectedTravelersValid = (ctx: ActivityContext): boolean => {
    const { selectedTravelerIds, userTravelers } =
        ctx[ParentState.activityTravelerInformation];

    if (selectedTravelerIds.length === 0) {
        return false;
    }

    return selectedTravelerIds.every((travelerId) => {
        const traveler = userTravelers.find((t) => t.id === travelerId);
        if (!traveler) {
            return false;
        }
        return travelerHasAllRequiredFields(traveler, ctx);
    });
};

